/*
 * Shoppable image hotspots — BLU-125
 * <hotspot-overlay> manages open/close of product popups on image markers.
 * - Click/tap toggles a popup (works on all devices, keyboard accessible).
 * - Desktop (hover-capable pointers): hover opens, mouse-leave closes.
 * - One popup open at a time across the page; outside-click / Esc closes.
 * - The popup stays anchored to the pin on all devices; it edge-flips/shifts
 *   so it never clips the viewport.
 */
(function () {
  if (customElements.get('hotspot-overlay')) return;

  window.Blundstone = window.Blundstone || {};

  const DESKTOP_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');

  // At most one hotspot is open at a time across the page.
  let openHotspot = null;

  function closeAll(except) {
    if (openHotspot && openHotspot !== except) openHotspot.close();
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll(null);
  });

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.hotspot')) closeAll(null);
  });

  class Hotspot {
    constructor(el) {
      this.el = el;
      this.marker = el.querySelector('.hotspot__marker');
      this.popup = el.querySelector('.hotspot__popup');
      this.closeBtn = el.querySelector('.hotspot__close');
      this.hoverTimer = null;
      if (!this.marker || !this.popup) return;

      this.marker.addEventListener('click', this.onClick.bind(this));
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });
      }

      // Hover only where the pointer supports it (never on touch).
      if (DESKTOP_HOVER.matches) {
        this.el.addEventListener('mouseenter', () => {
          clearTimeout(this.hoverTimer);
          this.open();
        });
        this.el.addEventListener('mouseleave', () => {
          this.hoverTimer = setTimeout(() => this.close(), 120);
        });
      }
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.el.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      closeAll(this);
      this.el.classList.add('is-open');
      this.marker.setAttribute('aria-expanded', 'true');
      openHotspot = this;
      // Clamp on every device — the popup is anchored to the pin everywhere now.
      this.position();
    }

    close() {
      this.el.classList.remove('is-open', 'is-flipped-y');
      this.el.style.removeProperty('--hotspot-shift');
      this.marker.setAttribute('aria-expanded', 'false');
      if (openHotspot === this) openHotspot = null;
    }

    // Desktop: keep the popup inside the viewport (vertical flip + horizontal shift).
    position() {
      this.el.classList.remove('is-flipped-y');
      this.el.style.removeProperty('--hotspot-shift');

      const margin = 8;
      let rect = this.popup.getBoundingClientRect();

      // Not enough room above the marker -> render below it.
      if (rect.top < margin) {
        this.el.classList.add('is-flipped-y');
        rect = this.popup.getBoundingClientRect();
      }

      // Horizontal: shift back on-screen if either edge overflows.
      let shift = 0;
      if (rect.left < margin) {
        shift = margin - rect.left;
      } else if (rect.right > window.innerWidth - margin) {
        shift = window.innerWidth - margin - rect.right;
      }
      if (shift !== 0) {
        this.el.style.setProperty('--hotspot-shift', `${Math.round(shift)}px`);
      }
    }
  }

  class HotspotOverlay extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('.hotspot').forEach((el) => {
        if (!el.__hotspot) el.__hotspot = new Hotspot(el);
      });
    }
  }

  customElements.define('hotspot-overlay', HotspotOverlay);
  window.Blundstone.HotspotOverlay = HotspotOverlay;
})();
